import {
  AddEmployeesToGroupRequest as AddEmployeesToGroupRequestDto,
  Application as ApplicationDto,
  BulkGetEmployeeGroupsResponse,
  Employee as EmployeeDto,
  Group as GroupDto,
  SortOrder,
  TriggerGroupChangeReviewRequest,
  UpdateGroupRequest,
} from '@electricjs/core_entity-client';

import { GroupMembersSortableColumns as SortableColumns } from '@/pages/People/EmployeeGroups/types';
import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import {
  BulkEmployeeGroupsResponse,
  Employee,
  GroupEmployeesData,
  GroupEmployeesResponse,
} from '@/types/employees';
import {
  AddEmployeesToGroupArgs,
  GetGroupApplicationsArgs,
  Group,
  GroupApplicationManagementArgs,
  PreviewGroupRuleMatchesArgs,
  RemoveEmployeeFromGroupArgs,
  RenameGroupArgs,
  TriggerDemoGroupChangeReviewArgs,
  TriggerGroupChangeReviewArgs,
  UpdateGroupArgs,
  UpdateGroupAutomationSettingsArgs,
  UpdateGroupImportEnabledArgs,
} from '@/types/groups';

import { transformEmployee } from '@/redux/slices/employeeApiSlice';
import {
  invalidatesList,
  invalidatesListAndById,
  providesListAndAllById,
} from './helpers/cacheTagHelpers';
import { transformApplication } from './applicationApiSlice';
import { Application } from '@/types/applications';

export const groupApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    removeEmployeeFromGroup: builder.mutation<
      void,
      RemoveEmployeeFromGroupArgs
    >({
      query: ({ organizationId, groupId, employeeId }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}/employees/${employeeId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, _error, { groupId, employeeId }) => [
        ...invalidatesListAndById(TagTypes.Group, groupId),
        ...invalidatesListAndById(
          TagTypes.GroupsAndApplicationsForEmployee,
          employeeId
        ),
        { type: TagTypes.GroupEmployees, id: groupId },
        { type: TagTypes.EmployeeGroups, id: employeeId },
        { type: TagTypes.EmployeeApplicationAccount, id: employeeId },
        { type: TagTypes.EmployeeApplicationsAndRequests, id: employeeId },
        TagTypes.Request,
        TagTypes.ProductSuggestions, // because product suggestions are based on the employee's groups
      ],
    }),
    addEmployeesToGroup: builder.mutation<Employee[], AddEmployeesToGroupArgs>({
      query: ({ organizationId, groupId, employeeIds }) => {
        const body: AddEmployeesToGroupRequestDto = {
          employee_ids: employeeIds,
        };

        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}/employees`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: EmployeeDto[]) => {
        return response?.map(employee => {
          return transformEmployee(employee);
        });
      },
      invalidatesTags: (_result, _error, { groupId, employeeIds }) => [
        ...invalidatesListAndById(TagTypes.Group, groupId),
        ...invalidatesList(TagTypes.GroupsAndApplicationsForEmployee),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeGroups,
          id: employeeId,
        })),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.GroupsAndApplicationsForEmployee,
          id: employeeId,
        })),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeApplicationsAndRequests,
          id: employeeId,
        })),
        { type: TagTypes.GroupEmployees, id: groupId },
        TagTypes.Request,
        TagTypes.ProductSuggestions, // because product suggestions are based on the employee's groups
      ],
    }),
    addApplicationsToGroup: builder.mutation<
      void,
      GroupApplicationManagementArgs
    >({
      query: ({ organizationId, groupId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}/applications/${applicationKey}`,
          method: 'POST',
        };
      },
      invalidatesTags: [
        TagTypes.Application,
        TagTypes.Group,
        TagTypes.GroupsAndApplicationsForEmployee,
        TagTypes.GroupsGrantingAccessForApplication,
      ],
    }),
    getGroupApplications: builder.query<
      Application[],
      GetGroupApplicationsArgs
    >({
      query: ({ organizationId, groupId }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}/applications`,
        };
      },
      providesTags: providesListAndAllById(TagTypes.Application),
      transformResponse: (response: Application[]) => {
        return response;
      },
    }),
    getGroupAncestorsApplications: builder.query<
      Application[],
      GetGroupApplicationsArgs
    >({
      query: ({ organizationId, groupId }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}/ancestors_applications`,
        };
      },
      providesTags: providesListAndAllById(TagTypes.Application),
      transformResponse: (applications: ApplicationDto[]) => {
        return applications.map(application =>
          transformApplication(application)
        );
      },
    }),
    removeApplicationFromGroup: builder.mutation<
      void,
      GroupApplicationManagementArgs
    >({
      query: ({ organizationId, groupId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}/applications/${applicationKey}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [
        TagTypes.Application,
        TagTypes.Group,
        TagTypes.GroupsAndApplicationsForEmployee,
        TagTypes.GroupsGrantingAccessForApplication,
      ],
    }),
    renameGroup: builder.mutation<void, RenameGroupArgs>({
      query: ({ organizationId, groupId, newName }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}`,
          method: 'PATCH',
          body: { name: newName },
        };
      },
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(TagTypes.Group, args.groupId),
        TagTypes.Group,
        TagTypes.GroupsGrantingAccessForApplication,
      ],
    }),
    updateGroup: builder.mutation<void, UpdateGroupArgs>({
      query: ({ organizationId, groupId, name, parentGroupId, ruleGroups }) => {
        const body: Omit<UpdateGroupRequest, 'parent_group_id'> & {
          parent_group_id?: string | null;
        } = { name };
        // Only touch parent_group_id when the caller actually passed one —
        // explicit null (EditGroupModal, to unparent a group) still clears
        // it, but omitting the key entirely (e.g. a rules-only save) must
        // leave the existing parent alone rather than defaulting to null.
        if (parentGroupId !== undefined) body.parent_group_id = parentGroupId;
        if (ruleGroups) body.rule_groups = ruleGroups;
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(TagTypes.Group, args.groupId),
        TagTypes.Group,
        TagTypes.GroupsGrantingAccessForApplication,
        // A rule_groups update changes which employees actually belong to
        // a dynamic group — without this, the Employees tab (getGroupEmployees,
        // tagged by groupId) would keep showing the pre-edit membership list.
        ...(args.ruleGroups
          ? [{ type: TagTypes.GroupEmployees, id: args.groupId }]
          : []),
      ],
    }),
    updateGroupImportEnabled: builder.mutation<
      void,
      UpdateGroupImportEnabledArgs
    >({
      query: ({ organizationId, groupId, groupName, importEnabled }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}`,
          method: 'PATCH',
          body: { name: groupName, import_enabled: importEnabled },
        };
      },
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(TagTypes.Group, args.groupId),
        TagTypes.Group,
      ],
    }),
    updateGroupAutomationSettings: builder.mutation<
      void,
      UpdateGroupAutomationSettingsArgs
    >({
      query: ({
        organizationId,
        groupId,
        groupName,
        joinAutomationPreference,
        leaveAutomationPreference,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}`,
          method: 'PATCH',
          body: {
            name: groupName,
            join_automation_preference: joinAutomationPreference,
            leave_automation_preference: leaveAutomationPreference,
          },
        };
      },
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(TagTypes.Group, args.groupId),
        TagTypes.Group,
      ],
    }),
    getGroupsForEmployeesBulk: builder.query<
      BulkEmployeeGroupsResponse,
      {
        organizationId: string;
        employeeIds: string[];
      }
    >({
      query: ({ organizationId, employeeIds }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees/groups/bulk`,
          method: 'POST',
          body: {
            employee_ids: employeeIds,
          },
        };
      },
      transformResponse: (
        response: BulkGetEmployeeGroupsResponse
      ): BulkEmployeeGroupsResponse => {
        return Object.entries<GroupDto[]>(
          response?.employee_groups ?? []
        ).reduce(
          (p, [key, groups]) => {
            p.employeeGroups[key] = groups.map(transformGroup);
            return p;
          },
          { employeeGroups: {} } as BulkEmployeeGroupsResponse
        );
      },
      providesTags: (_result, _error, { employeeIds }) => [
        { type: TagTypes.EmployeeGroups, id: 'LIST' },
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeGroups as const,
          id: employeeId,
        })),
      ],
    }),
    triggerGroupChangeReview: builder.mutation<
      void,
      TriggerGroupChangeReviewArgs
    >({
      query: ({
        organizationId,
        employeeId,
        groupsAdded,
        groupsRemoved,
        assigneeUserId,
      }) => {
        const body: TriggerGroupChangeReviewRequest = {
          groups_added: groupsAdded,
          groups_removed: groupsRemoved,
          assignee_user_id: assigneeUserId,
        };

        return {
          url: `/v1/organizations/${organizationId}/employees/${employeeId}/group-change-review`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TagTypes.Request],
    }),
    triggerDemoGroupChangeReview: builder.mutation<
      void,
      TriggerDemoGroupChangeReviewArgs
    >({
      query: ({
        organizationId,
        employeeId,
        groupsAdded,
        groupsRemoved,
        hrisProviderSlug,
        assigneeUserId,
      }) => {
        const body = {
          groups_added: groupsAdded,
          groups_removed: groupsRemoved,
          hris_provider_slug: hrisProviderSlug,
          assignee_user_id: assigneeUserId,
        };

        return {
          url: `/v1/organizations/${organizationId}/employees/${employeeId}/demo-group-change-review`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TagTypes.Request, TagTypes.Organization],
    }),
    getGroupEmployees: builder.query<
      GroupEmployeesData,
      {
        organizationId: string;
        groupId: string;
        params?: {
          sortOrder?: SortOrder;
          orderBy?: SortableColumns;
          limit?: number;
          offset?: number;
          includeSubgroupEmployees?: boolean;
          searchTerm?: string;
        };
      }
    >({
      query: ({ organizationId, groupId, params = {} }) => {
        const {
          sortOrder,
          orderBy,
          limit,
          offset,
          includeSubgroupEmployees,
          searchTerm,
        } = params;
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}/employees`,
          params: {
            ...(sortOrder ? { sort_order: sortOrder } : {}),
            ...(orderBy ? { order_by: params.orderBy } : {}),
            ...(limit ? { limit } : {}),
            ...(offset ? { offset } : {}),
            ...(includeSubgroupEmployees
              ? { include_subgroup_employees: true }
              : {}),
            ...(searchTerm ? { search_term: searchTerm } : {}),
          },
        };
      },
      transformResponse: (
        response: GroupEmployeesResponse
      ): GroupEmployeesData => {
        return {
          employees: response?.results?.map(transformEmployee) ?? [],
          total: response?.total ?? 0,
        };
      },
      providesTags: (results, _error, args) => [
        ...providesListAndAllById(TagTypes.Employee)(results?.employees ?? []),
        { type: TagTypes.GroupEmployees, id: args.groupId },
      ],
    }),
    previewGroupRuleMatches: builder.query<
      GroupEmployeesData,
      PreviewGroupRuleMatchesArgs
    >({
      query: ({
        organizationId,
        ruleGroups,
        parentGroupId,
        limit,
        offset,
      }) => ({
        url: `/v1/organizations/${organizationId}/groups/preview/employees`,
        method: 'POST',
        params: {
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
        },
        body: {
          rule_groups: ruleGroups,
          ...(parentGroupId ? { parent_group_id: parentGroupId } : {}),
        },
      }),
      transformResponse: (
        response: GroupEmployeesResponse
      ): GroupEmployeesData => ({
        employees: response?.results?.map(transformEmployee) ?? [],
        total: response?.total ?? 0,
      }),
    }),
  }),
});
export const transformGroup = (group: GroupDto): Group => {
  return {
    id: group.id,
    name: group.name,
    nameFrCa: group.name_fr_ca,
    createdAt: group.created_at,
    updatedAt: group.updated_at,
    parentGroupId: group.parent_group_id ?? '',
    importEnabled: group.import_enabled,
    hrisGroupIntegration: group.hris_group_integration
      ? {
          sourceId: group.hris_group_integration.source_id,
          hrisIntegrationId: group.hris_group_integration.hris_integration_id,
        }
      : undefined,
    applicationsCount: group.applications_count,
    employeesCount: group.employees_count,
    joinAutomationPreference: group.join_automation_preference ?? null,
    leaveAutomationPreference: group.leave_automation_preference ?? null,
    membershipType: group.membership_type,
    ruleGroups: group.rule_groups ?? [],
  };
};
export const {
  useRemoveEmployeeFromGroupMutation,
  useRenameGroupMutation,
  useUpdateGroupMutation,
  useAddEmployeesToGroupMutation,
  useAddApplicationsToGroupMutation,
  useGetGroupApplicationsQuery,
  useGetGroupAncestorsApplicationsQuery,
  useGetGroupEmployeesQuery,
  useGetGroupsForEmployeesBulkQuery,
  useLazyGetGroupEmployeesQuery,
  useLazyGetGroupApplicationsQuery,
  useLazyGetGroupAncestorsApplicationsQuery,
  useRemoveApplicationFromGroupMutation,
  useUpdateGroupImportEnabledMutation,
  useUpdateGroupAutomationSettingsMutation,
  useTriggerGroupChangeReviewMutation,
  useTriggerDemoGroupChangeReviewMutation,
  useLazyPreviewGroupRuleMatchesQuery,
} = groupApiSlice;
